import { z } from 'zod';
import {
  calendarDateSchema,
  calendarDateToIsoEnd,
  calendarDateToIsoStart,
} from '@/features/listing-filters/calendarDate';
import type { SortDirection } from '@/lib/tableSort';

/** Mirrors the API's listClinics and listPets inputs by hand, like `types.ts`: apps/admin cannot import @astro/db. */
export const TIERS = ['definitive', 'presumptive', 'screening'] as const;

export const CLINIC_SORT_KEYS = [
  'name',
  'petsWithEvidence',
  'petsWithCondition',
  'definitivePets',
  'presumptivePets',
  'screeningPets',
  'liveSignals',
  'conditionsSeen',
  'lastCapturedAt',
] as const;

export const PET_SORT_KEYS = [
  'lastCapturedAt',
  'signalCount',
  'liveSignalCount',
  'conditionCount',
  'highestTier',
] as const;

export type ClinicSortKey = (typeof CLINIC_SORT_KEYS)[number];
export type PetSortKey = (typeof PET_SORT_KEYS)[number];
export { nextSort, type SortDirection, type SortState } from '@/lib/tableSort';

/** Names read A to Z first; every count and date reads largest or newest first. */
export const firstClinicSortDirection = (key: ClinicSortKey): SortDirection =>
  key === 'name' ? 'asc' : 'desc';

/** Page 1 stays out of the URL, so the default view has a clean link. */
export const pageParam = (page: number): number | undefined => (page > 1 ? page : undefined);

export type ListingView = 'error' | 'loading' | 'empty' | 'rows';

/** An error is checked first: shown as empty, it would send QA after the wrong bug. */
export const listingView = (
  query: { isLoading: boolean; isError: boolean },
  rowCount: number
): ListingView => {
  if (query.isError) return 'error';
  if (query.isLoading) return 'loading';
  return rowCount === 0 ? 'empty' : 'rows';
};

/** The old dropdown's `?clinicId=&page=` paged that clinic's pets, which the clinic page now does. */
export const legacyClinicTarget = (search: { clinicId?: string; page?: number }) =>
  search.clinicId ? { clinicId: search.clinicId, page: search.page } : null;

const sortDirectionSchema = z.enum(['asc', 'desc']);
/** Both chosen means either, which is no filter at all. */
const yesNoSchema = z.array(z.enum(['yes', 'no']));

/**
 * Malformed values fall back to undefined via `.catch()` rather than throwing,
 * so a hand-edited URL degrades to an unfiltered list instead of a blank page.
 */
const conditionFilterFields = {
  condition: z.array(z.string().min(1).max(30)).max(50).optional().catch(undefined),
  tier: z.array(z.enum(TIERS)).optional().catch(undefined),
  hasCondition: yesNoSchema.optional().catch(undefined),
  capturedFrom: calendarDateSchema.optional().catch(undefined),
  capturedTo: calendarDateSchema.optional().catch(undefined),
};

const pageField = { page: z.number().int().min(1).optional().catch(undefined) };

/** No page number: a saved page would drop you into the middle of a list read days ago. */
export const clinicListFiltersSchema = z.object({
  ...conditionFilterFields,
  search: z.string().min(1).max(200).optional().catch(undefined),
  sortBy: z.enum(CLINIC_SORT_KEYS).optional().catch(undefined),
  sortDirection: sortDirectionSchema.optional().catch(undefined),
});

const clinicListPageSchema = clinicListFiltersSchema.extend(pageField);

/** `clinicId` is the old dropdown's link shape, kept only so the route can redirect it. */
export const clinicListSearchSchema = clinicListPageSchema.extend({
  clinicId: z.string().min(1).max(30).optional().catch(undefined),
});

export const petListSearchSchema = z.object({
  ...conditionFilterFields,
  /** The clinic list as it was when this clinic was opened, page included, so Back returns there. */
  list: clinicListPageSchema.optional().catch(undefined),
  hasLiveSignals: yesNoSchema.optional().catch(undefined),
  sortBy: z.enum(PET_SORT_KEYS).optional().catch(undefined),
  sortDirection: sortDirectionSchema.optional().catch(undefined),
  ...pageField,
});

export type ClinicListFilters = z.infer<typeof clinicListFiltersSchema>;
export type PetListSearch = z.infer<typeof petListSearchSchema>;

/** The filters both lists share, plus the pets-only live signal toggle. */
export interface ConditionFilterValues {
  condition?: string[];
  tier?: (typeof TIERS)[number][];
  hasCondition?: ('yes' | 'no')[];
  hasLiveSignals?: ('yes' | 'no')[];
  capturedFrom?: string;
  capturedTo?: string;
}

export const EMPTY_CONDITION_FILTERS = {
  condition: undefined,
  tier: undefined,
  hasCondition: undefined,
  hasLiveSignals: undefined,
  capturedFrom: undefined,
  capturedTo: undefined,
} satisfies Record<keyof ConditionFilterValues, undefined>;

export const yesNoToBoolean = (values: ('yes' | 'no')[] | undefined): boolean | undefined =>
  values?.length === 1 ? values[0] === 'yes' : undefined;

/** URL values to the API's filter input. Dates become local-day bounds. */
export const toConditionFilterInput = (values: ConditionFilterValues) => ({
  conditionConceptIds: values.condition?.length ? values.condition : undefined,
  tiers: values.tier?.length ? values.tier : undefined,
  hasCondition: yesNoToBoolean(values.hasCondition),
  capturedFrom: calendarDateToIsoStart(values.capturedFrom),
  capturedTo: calendarDateToIsoEnd(values.capturedTo),
});
